/* External dependencies */
import * as AWSCognito from 'amazon-cognito-identity-js';

/* Local dependencies */
import { LoginRequest } from '../components/dashboard/auth/login/redux/actions';
import {
  initClientSucceeded,
  SendVerificationCodeRequest,
  SessionUser,
} from '../components/dashboard/auth/redux/auth-actions';
import { mapCognitoError } from './cognito-errors';
import CustomCognitoUserSession from './cognitoUserSession';

interface Token {
  payload: SessionUser;
  jwtToken: string;
}

interface RefreshToken {
  token: string;
}
export interface Session {
  accessToken?: Token;
  email: string;
  email_verified: Boolean;
  family_name: string;
  given_name: string;
  idToken?: Token;
  newPasswordRequired?: Boolean;
  phone_number: Number;
  phone_number_verified: Boolean;
  refreshToken?: RefreshToken;
}

const asyncAuthenticateUser = (cognitoUser, cognitoAuthenticationDetails): Promise<CustomCognitoUserSession> => {
  return new Promise((resolve, reject) => {
    cognitoUser.authenticateUser(cognitoAuthenticationDetails, {
      onSuccess: resolve,
      onFailure: reject,
      newPasswordRequired: (userAttributes) => {
        reject(new Error('NEW_PASSWORD_REQUIRED'));
      },
    });
  });
};

export async function customAuthSignIn({ username, password, userPool }: LoginRequest) {
  const cognitoUserPool = new AWSCognito.CognitoUserPool(userPool);
  const userData = { Username: username, Pool: cognitoUserPool };
  const cognitoUser = new AWSCognito.CognitoUser(userData);

  const cognitoAuthenticationDetails = new AWSCognito.AuthenticationDetails({
    Username: username,
    Password: password,
  });

  try {
    const session = await asyncAuthenticateUser(cognitoUser, cognitoAuthenticationDetails);

    return initClientSucceeded(session);
  } catch (err) {
    throw mapCognitoError(err);
  }
}

const asyncSendCustomChallengeAnswer = (cognitoUser, verificationCode): Promise<CustomCognitoUserSession> => {
  return new Promise((resolve, reject) => {
    cognitoUser.sendCustomChallengeAnswer(verificationCode, {
      onSuccess: resolve,
      onFailure: reject,
    });
  });
};

export async function sendVerificationCode({
  currentUserSession,
  verificationCode,
  username,
  userPool,
}: SendVerificationCodeRequest) {
  const cognitoUserPool = new AWSCognito.CognitoUserPool(userPool);
  const userData = { Username: username, Pool: cognitoUserPool };
  const cognitoUser = new AWSCognito.CognitoUser(userData);

  (cognitoUser as any).Session = currentUserSession;

  try {
    const session: CustomCognitoUserSession = await asyncSendCustomChallengeAnswer(cognitoUser, verificationCode);

    return initClientSucceeded(session);
  } catch (err) {
    throw mapCognitoError(err);
  }
}

const asyncSignUp = (
  cognitoUserPool: AWSCognito.CognitoUserPool,
  email: string,
  password: string,
  attributeList: AWSCognito.CognitoUserAttribute[],
): Promise<AWSCognito.ISignUpResult> => {
  return new Promise((resolve, reject) => {
    cognitoUserPool.signUp(email, password, attributeList, null, (err, result) => {
      if (err) {
        reject(err);

        return;
      }

      resolve(result);
    });
  });
};

export interface RegisterRequest {
  email: string;
  password: string;
  givenName: string;
  familyName: string;
  userPool: any;
  phone: string;
}

export async function registerUser({ email, password, givenName, familyName, userPool, phone }: RegisterRequest) {
  const cognitoUserPool = new AWSCognito.CognitoUserPool(userPool);

  const attributeList: AWSCognito.CognitoUserAttribute[] = [
    new AWSCognito.CognitoUserAttribute({
      Name: 'email',
      Value: email,
    }),
    new AWSCognito.CognitoUserAttribute({
      Name: 'given_name',
      Value: givenName,
    }),
    new AWSCognito.CognitoUserAttribute({
      Name: 'family_name',
      Value: familyName,
    }),
    new AWSCognito.CognitoUserAttribute({
      Name: 'phone_number',
      Value: phone,
    }),
  ];

  try {
    const result = await asyncSignUp(cognitoUserPool, email, password, attributeList);

    return { success: true, user: result.user, userSub: result.userSub };
  } catch (err) {
    throw mapCognitoError(err);
  }
}

const asyncConfirmRegistration = (cognitoUser: AWSCognito.CognitoUser, confirmationCode: string): Promise<any> => {
  return new Promise((resolve, reject) => {
    cognitoUser.confirmRegistration(confirmationCode, true, (err, result) => {
      if (err) {
        reject(err);

        return;
      }

      resolve(result);
    });
  });
};

export interface ConfirmRegistrationRequest {
  username: string;
  confirmationCode: string;
  userPool: any;
}

export async function confirmRegistration({ username, confirmationCode, userPool }: ConfirmRegistrationRequest) {
  const cognitoUserPool = new AWSCognito.CognitoUserPool(userPool);
  const userData = { Username: username, Pool: cognitoUserPool };
  const cognitoUser = new AWSCognito.CognitoUser(userData);

  try {
    const result = await asyncConfirmRegistration(cognitoUser, confirmationCode);

    return { success: true, result };
  } catch (err) {
    throw mapCognitoError(err);
  }
}

// Forgot Password
const asyncForgotPassword = (cognitoUser): Promise<any> => {
  return new Promise((resolve, reject) => {
    cognitoUser.forgotPassword({
      onSuccess: resolve,
      onFailure: reject,
    });
  });
};

export interface ForgotPasswordRequest {
  username: string;
  userPool: any;
}

export async function forgotPasswordRequest({ username, userPool }: ForgotPasswordRequest) {
  const cognitoUserPool = new AWSCognito.CognitoUserPool(userPool);
  const userData = { Username: username, Pool: cognitoUserPool };
  const cognitoUser = new AWSCognito.CognitoUser(userData);

  try {
    const result = await asyncForgotPassword(cognitoUser);

    return { success: true, result };
  } catch (err) {
    throw mapCognitoError(err);
  }
}

// Confirm New Password
const asyncConfirmPassword = (cognitoUser, verificationCode: string, newPassword: string): Promise<any> => {
  return new Promise((resolve, reject) => {
    cognitoUser.confirmPassword(verificationCode, newPassword, {
      onSuccess: resolve,
      onFailure: reject,
    });
  });
};

export interface ConfirmForgotPasswordRequest {
  username: string;
  verificationCode: string;
  newPassword: string;
  userPool: any;
}

export async function confirmForgotPasswordRequest({
  username,
  verificationCode,
  newPassword,
  userPool,
}: ConfirmForgotPasswordRequest) {
  const cognitoUserPool = new AWSCognito.CognitoUserPool(userPool);
  const userData = { Username: username, Pool: cognitoUserPool };
  const cognitoUser = new AWSCognito.CognitoUser(userData);

  try {
    const result = await asyncConfirmPassword(cognitoUser, verificationCode, newPassword);

    return { success: true, result };
  } catch (err) {
    throw mapCognitoError(err);
  }
}
